import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useLegacyImportedCase } from "../../../hooks/useLegacyCases";
import LegacyDocumentPanel from "./LegacyDocumentPanel";
import LegacyReviewedNarratives from "./LegacyReviewedNarratives";
import LegacySendOutAction from "./LegacySendOutAction";
import { useAuthStore } from "../../../store";
import { canReviewLegacyReports } from "../../../utils/legacyCaseAccess";
import { useHasPermission } from "../../../hooks/useHasPermission";
import LegacyTechnicalReportNotice from "./LegacyTechnicalReportNotice";

export default function LegacyImportedCasePanel({
  caseId,
}: {
  caseId: string;
}) {
  const query = useLegacyImportedCase(caseId);
  const profile = useAuthStore((state) => state.profile);
  const canAmend = useHasPermission("sign_off.amend");
  const isTechnicalReport = useMemo(
    () => query.data?.reportKind === "technical",
    [query.data?.reportKind]
  );
  const handleRetry = useCallback(() => {
    void query.refetch();
  }, [query]);
  if (query.isLoading)
    return (
      <Skeleton height={400} role="status" aria-label="Loading legacy report" />
    );
  if (query.error || !query.data)
    return (
      <Alert color="red" title="Original report unavailable">
        <Stack align="flex-start">
          <Text>
            {query.error?.message || "The archive could not be loaded."}
          </Text>
          <Button onClick={handleRetry}>Retry original report</Button>
        </Stack>
      </Alert>
    );
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Title order={2}>
            {isTechnicalReport
              ? "Original NovoPath Technical Summary"
              : "Original NovoPath report"}
          </Title>
          <Badge variant="light">Legacy imported</Badge>
        </Group>
        <LegacySendOutAction
          caseId={caseId}
          caseNumber={query.data.sourceCaseNumber}
        />
      </Group>
      {isTechnicalReport ? (
        <LegacyTechnicalReportNotice caseId={caseId} showCaseLink={false} />
      ) : (
        <Alert
          color={query.data.canIssueReportRevisions ? "green" : "blue"}
          title={
            query.data.canIssueReportRevisions
              ? "Historical reconstruction approved"
              : "Historical reconstruction requires approval"
          }
        >
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              {query.data.canIssueReportRevisions
                ? "The approved historical baseline is preserved. Authorized clinical reviewers can issue an addendum, amendment, or correction with current attribution."
                : "Approve the report reconstruction before creating an addendum, amendment, or correction. The original report remains available below."}
            </Text>
            {query.data.latestNativeReportVersion ? (
              <Text size="sm">
                A newer LIS report has been issued (version{" "}
                {query.data.latestNativeReportVersion}). Use View Report or
                Download Report for that version. The PDF below is the original
                NovoPath report.
              </Text>
            ) : (
              <Text size="sm">
                The original remains the issued report until a new revision is
                signed.
              </Text>
            )}
            <Group>
              {canReviewLegacyReports(profile) && (
                <Button
                  component={Link}
                  variant="light"
                  to={`/admin/legacy-cases/reports/${caseId}`}
                >
                  {query.data.approvedBaselineId
                    ? "View approved reconstruction"
                    : "Review report reconstruction"}
                </Button>
              )}
              {query.data.canIssueReportRevisions && canAmend && (
                <Button component={Link} to={`/pathologist-review/${caseId}`}>
                  Open report revision workflow
                </Button>
              )}
            </Group>
          </Stack>
        </Alert>
      )}
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <LegacyDocumentPanel
            documents={query.data.documents}
            initialDocumentId={query.data.review.reportDocumentId}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <LegacyReviewedNarratives review={query.data.review} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
